import {
    useContext,
    useEffect,
    type FC,
    useRef
} from "react";
import {
    View
} from "react-native";
import type IStepperMarkerProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useReducedMotion
} from "../../../../helpers/motion";
import {
    usePointerType
} from "../../../../helpers/pointer";
import type {
    StepperItemPartContentProps
} from "../../type";
import {
    TriangleAlert as TriangleAlertIcon,
    Check as CheckIcon
} from "lucide-react-native";
import {
    StepperStepStateContext,
    withStepperItemContext
} from "../../context";
import {
    getStepperMeasures,
    getStepperColors
} from "../../stylesheet";
import {
    formatNumber
} from "../../../../helpers/locale";
import AnimatedIcon from "../../../animatedIcon";
import Icon from "../../../icon";
import Text from "../../../text";

const StepperMarkerContent: FC<StepperItemPartContentProps<IStepperMarkerProps>> = ({
    customTheme: customThemeProp,
    children,
    context,
    style,
    item,
    ...props
}) => {
    const customTheme = customThemeProp ?? context.customTheme;

    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        typography,
        durations,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        isHighlighted
    } = useContext(StepperStepStateContext);

    const pointerType = usePointerType();

    const isReduceMotionEnabled = useReducedMotion();

    const isInitialCompletedRef = useRef(item.status === "completed");

    const measures = getStepperMeasures({
        orientation: context.orientation,
        isInSheet: context.isInSheet,
        size: context.size,
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        pointerType,
        typography,
        iconSizes,
        borders,
        spaces
    });

    const currentColors = getStepperColors({
        status: item.status,
        isHighlighted,
        colors
    });

    const {
        container: containerDynamicStyle
    } = useStyles({
        isCurrent: item.status === "current",
        colors: currentColors,
        isReduceMotionEnabled,
        durations,
        measures,
        radiuses,
        borders
    });

    useEffect(() => {
        if(item.status !== "completed") {
            isInitialCompletedRef.current = false;
        }
    }, [
        item.status
    ]);

    const renderContent = () => {
        if(item.status === "completed") {
            return <AnimatedIcon
                animation={isInitialCompletedRef.current ? "none" : "draw"}
                customColor={currentColors.markerContentColor}
                customTheme={customTheme}
                size={measures.iconSize}
                icon={CheckIcon}
            />;
        }

        if(item.status === "error") {
            return <Icon
                customColor={currentColors.markerContentColor}
                customTheme={customTheme}
                size={measures.iconSize}
                icon={TriangleAlertIcon}
            />;
        }

        return <Text
            variant={item.status === "current" ? measures.markerSelectedVariant : measures.markerVariant}
            customColor={currentColors.markerContentColor}
            style={stylesheet.number}
            customTheme={customTheme}
        >
            {formatNumber({
                locale: context.activeLocale,
                value: item.index + 1
            })}
        </Text>;
    };

    return <View
        {...props}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
        aria-hidden={true}
    >
        {children ?? renderContent()}
    </View>;
};
export default withStepperItemContext("Marker", StepperMarkerContent);
